import { Collapse, CollapseProps as AntCollapseProps } from 'antd';
import noop from 'lodash/noop';
import { FC, forwardRef, ReactNode, useEffect, useState } from 'react';
import styled, { CSSProperties } from 'styled-components';

import { BodilColor, NorlysFont } from '~src/branding';
import { boxShadows } from '~src/branding/boxShadow';
import { Icon as Icons } from '~src/components/Icon';
import { IconConstructor } from '~src/model';

import { Button, ButtonNorlys } from './Button';
import { Chevron } from './Chevron';
import { Heading } from './Typography';

const { Panel } = Collapse;

const Wrapper = styled.div<{ padding?: CSSProperties['padding']; collapseStyle?: CSSProperties }>(
  ({ padding, collapseStyle }) => ({
    width: '100%',
    margin: '8px 0',
    '.ant-collapse': { ...collapseStyle, padding: padding ?? '32px' },
    '.ant-collapse > .ant-collapse-item > .ant-collapse-header': {
      display: 'flex',
      alignItems: 'center',
    },
    '.ant-typography': { marginBottom: 0 },
  })
);

type Props = AntCollapseProps & {
  open?: boolean;
  setOpen?: (value: boolean) => void;
  done?: boolean;
  Icon?: IconConstructor;
  title?: string;
  header?: ReactNode;
  children?: ReactNode;
  editButton?: { label: string } | false;
  collapseStyle?: CSSProperties;

  ProceedSection?: FC<{ onClick: () => void; disabled?: boolean }>;

  editable?: boolean;
  setEditable?: (editable: boolean) => void;

  hideChevron?: boolean;
  onClick?: () => void;
  onProceed?: () => void;
  proceedText?: string;
  proceedDisabled?: boolean;
  isNorlys?: boolean;
};

const CollapsiblePanelBase = forwardRef<HTMLDivElement, Props>(function CollapsiblePanelBase(props: Props, ref) {
  const {
    open: openInput,
    setOpen: setOpenInput,
    children,
    bordered = false,
    expandIconPosition = 'end',
    header,
    collapseStyle,
    editButton = false,
    style,
    onClick,
    onProceed,
    ProceedSection,
    proceedDisabled,
    hideChevron,
    ...rest
  } = props;

  const [open, setOpen] = useState(openInput);

  useEffect(() => setOpen(openInput), [openInput]);

  const updateOpen = (value: boolean) => {
    setOpen(value);
    setOpenInput?.(value);
  };

  return (
    <Wrapper style={style} padding={style?.padding} collapseStyle={collapseStyle} ref={ref}>
      <Collapse
        activeKey={open ? '1' : '0'}
        bordered={bordered}
        // eslint-disable-next-line sonarjs/no-use-of-empty-return-value
        onChange={() => (onClick ? onClick() : updateOpen?.(!open))}
        expandIconPosition={expandIconPosition}
        expandIcon={() => false}
        {...rest}
      >
        <Panel
          extra={
            hideChevron ? undefined : (
              <div style={{ display: 'flex', flexFlow: 'row nowrap', alignItems: 'center' }}>
                {!!editButton && (
                  <Button style={{ margin: 0 }} size='small' onClick={noop}>
                    {editButton.label}
                  </Button>
                )}
                <Chevron style={{ margin: '0 8px' }} open={open} />
              </div>
            )
          }
          header={header ?? <Title {...props} />}
          key='1'
        >
          {children}
          {onProceed && ProceedSection && <ProceedSection {...{ onClick: onProceed, disabled: proceedDisabled }} />}
        </Panel>
      </Collapse>
    </Wrapper>
  );
});

const Title = ({ done, title, Icon, isNorlys }: Pick<Props, 'title' | 'done' | 'Icon' | 'isNorlys'>) => {
  const iconProps = { height: 25, width: 25, style: { marginRight: '8px' } };

  return (
    <div style={{ display: 'flex', flexFlow: 'row nowrap', alignItems: 'center' }}>
      {done && !isNorlys && <Icons.Completed {...iconProps} />}
      {!done && Icon && <Icon {...iconProps} />}
      {isNorlys ? <p style={{ ...NorlysFont.style('heading', 20) }}>{title}</p> : <Heading>{title}</Heading>}
    </div>
  );
};

export const CollapsiblePanel = forwardRef<HTMLDivElement, Props>(function CollapsiblePanel(
  { editable, setEditable, ...props }: Props,
  ref
) {
  return (
    <CollapsiblePanelBase
      collapseStyle={{ borderRadius: '32px', backgroundColor: BodilColor.base.smoke }}
      ProceedSection={({ onClick, disabled }) => (
        <div
          style={{
            width: '100%',
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'center',
            margin: '8px 0 16px 0',
            gap: '16px',
          }}
        >
          {!!setEditable && (
            <Button {...{ onClick: () => setEditable?.(!editable) }}>{editable ? 'Gem' : 'Ret boligdata'}</Button>
          )}

          <Button {...{ onClick, type: 'callToAction', disabled }}>{props.proceedText ?? 'Videre'}</Button>
        </div>
      )}
      ref={ref}
      {...props}
    />
  );
});

export const CollapsiblePanelNorlys = forwardRef<HTMLDivElement, Props>(function CollapsiblePanelNorlys(
  props: Props,
  ref
) {
  return (
    <CollapsiblePanelBase
      {...{
        editButton: false,
        style: { paddingBottom: 0, ...boxShadows.norlys },
        Icon: undefined,
        Button: ButtonNorlys,
        isNorlys: true,
        ref,
        ...props,
      }}
      ProceedSection={({ onClick, disabled }) => (
        <div
          style={{
            width: '100%',
            display: 'flex',
            justifyContent: 'center',
            margin: '8px 0 16px 0',
          }}
        >
          <ButtonNorlys {...{ onClick, disabled, style: { margin: '24px 8px 0 0' } }}>
            {props.proceedText ?? 'Gå videre'}
          </ButtonNorlys>
        </div>
      )}
    />
  );
});
